import type {
  AgeBand,
  Allergen,
  ChargeDay,
  Guest,
  MealPrestation,
  NightPrice,
  PmsReservationStatus,
  ProductRequest,
  RatePlan,
  ReservationView,
  Room,
  RoomStayInput,
  RoomStayView,
  SoldTerms,
  StayPerson,
  StayPersonInput,
  StayPersonMeal,
  StayPersonNeeds,
} from '@lemontaigu/pms-core';
import {
  pmsAddDays,
  pmsAnonymousPersons,
  pmsCountsFromPersons,
  pmsMealDefault,
  pmsNightCount,
  pmsNightVector,
  type PmsBoard,
} from '@lemontaigu/pms-core';
import { isoAddDays, parseEurosToCents } from './logic.js';

// The booking form's STATE and its payload builder, extracted from
// Reservations.tsx (P1.7 step 4).
//
// Extracted rather than copied, for the reason `UnitFields` was: « Plan
// d'occupation » now starts bookings too, and two forms that disagree about
// what a booking IS would be a bug nobody notices until a booking made from one
// screen reads wrongly on the other. One definition, two mounts.
//
// Everything here is pure, so the arithmetic that decides what reaches the
// database is testable without a browser.
//
// ── A booking is a GUEST plus a LIST OF ROOMS (multi-room step 6b) ──────────
//
// … and since group edit step 4 (PMS-GROUP-EDIT-BRIEF), a room is a LIST OF
// PERSONS. The adults / children / infants a room used to carry as typed
// numbers are gone from the draft: they are counts of its persons, derived
// wherever they are needed, so the draft cannot hold two answers to « how many
// are in room 17 ». The bulk actions of the form (« Pour tous », the selection
// row) are functions here: they edit the draft, and saving sends one command —
// no bulk RPC, which would be a second write path for the same aggregate (§4).

/** One meal exception, as the draft holds it: the person differs from the
 *  room's default on this date. */
export type MealException = Omit<StayPersonMeal, 'personId'>;

/** A person's needs and allergens, as the form holds them (group edit step 5).
 *  Health data: only ever filled in for a desk holding `pms.needs.read`. */
export type PersonHealth = {
  /** '' = nothing declared. */
  needs: string;
  allergens: Allergen[];
};

/** One person in a room, as the form holds it. */
export type PersonDraft = {
  /** React's key — stable across edits, never sent. */
  key: string;
  /** The stored `pms_stay_person.id`, or null for a person being added. */
  id: string | null;
  /** '' = « Adulte 2 » until the rooming list names them. */
  name: string;
  ageBand: AgeBand;
  startDate: string;
  endDate: string;
  /** Exceptions ONLY. A toggle that lands back on the default removes the
   *  exception rather than storing a redundant one, so a later room switch
   *  still reaches this person (Q6 keeps only real opt-outs). */
  exceptions: MealException[];
  /** Needs and allergens. null = not loaded, or not this desk's to see: saving
   *  then sends neither key and the database keeps what it holds (the patch
   *  rule), so a desk without the right never erases them. A person ADDED by a
   *  holder starts null and gets a value when something is typed. */
  health: PersonHealth | null;
};

/** One logement, as the form holds it while being typed into. */
export type RoomDraft = {
  /** The stored `pms_room_stay.id`, or null for a room being added.
   *
   *  This is the field that makes the payload an edit rather than a rewrite:
   *  the writer updates a room that carries one, creates a room without one,
   *  and RELEASES any stored logement the payload does not name. */
  id: string | null;
  roomTypeId: string;
  /** The category the logement was SOLD as ('' = not known): read-only, shown in
   *  its own column (« Cat. vendue »), never written back — the payload names its keys. */
  soldRoomTypeId: string;
  /** Seeded from Vega: an empty `soldRoomTypeId` then means Vega's plan has no
   *  line for this logement, not « the booked category ». Absent = not from Vega. */
  fromVega?: boolean;
  /** The cancellation terms the logement was sold under (PB7b) — read-only,
   *  shown under the room, never written back (the writer copies them itself). */
  cancellationTerms?: SoldTerms | null;
  /** The room's OWN rate plan — what its type de séjour comes from. '' = the
   *  établissement's default, for a room being created. Kept per room because
   *  a group mixes DP, Pdj and Ch rooms, and saving must not flatten them all
   *  onto one plan. */
  ratePlanId: string;
  /** The unit, when the booking already knows it — set by a click on a free
   *  cell in the rack, or carried from what is already assigned. '' = let the
   *  desk assign later. */
  roomId: string;
  checkin: string;
  checkout: string;
  /** Who is in the room. Never empty. */
  persons: PersonDraft[];
  /** False while a stored room's persons have not been read yet: the persons
   *  above are a stand-in drawn from its counts, and saving then sends no
   *  `persons` key — the database keeps what it holds (step 3's patch rule)
   *  rather than being told a list nobody loaded. */
  personsLoaded: boolean;
  /** The room's meal switches. null = not read (the database keeps them). */
  meals: MealPrestation[] | null;
  /** '' means « the booker ». A NAME, never a guest reference (0086). */
  occupantName: string;
  /** THIS ROOM's real status, so the edit rule is read per logement (0091).
   *  null while creating. */
  origStatus: PmsReservationStatus | null;
  /** The stored dates, kept so « extend only » has something to compare to. */
  origCheckin: string;
  origCheckout: string;
  /** The FALLBACK price for THIS room: what a night the desk has not touched
   *  costs, and what « appliquer à toutes les nuits » writes across its
   *  vector. */
  priceEuros: string;
  /** The per-night breakdown for this room, in stay order, in CENTS. */
  nights: NightPrice[];
  /** The agreed extras asked for ON THIS ROOM. The COMPLETE set — what is not
   *  here is not billed (0084's rule), which is why the form loads a stay's
   *  lines before editing. Meals are NOT here: they are switches and persons. */
  products: ProductRequest[];
};

export type FormState = {
  id: string | null;
  guestId: string; // '' = new guest
  lastName: string;
  firstName: string;
  email: string;
  phone: string;
  lang: string;
  /** Never empty: a booking with no logement cannot be represented, and the
   *  writer refuses one. `removeRoom` will not take the last. */
  rooms: RoomDraft[];
  /** Booking-level, and create-only. Once a booking exists its status is
   *  DERIVED from its logements (0091). */
  status: 'reserved' | 'confirmed';
  channel: string;
  channelRef: string;
  notes: string;
  /** Display only — the booking's group name, when it is one. */
  groupName: string;
};

// ── Persons ─────────────────────────────────────────────────────────────────

let personSeq = 0;
const nextKey = () => `p${++personSeq}`;

export const newPerson = (ageBand: AgeBand, startDate: string, endDate: string): PersonDraft => ({
  key: nextKey(),
  id: null,
  name: '',
  ageBand,
  startDate,
  endDate,
  exceptions: [],
  health: null,
});

/** Anonymous persons for counted heads, the whole stay — the form's mirror of
 *  what the database creates for « 2 adultes ». */
const personsFromCounts = (adults: number, children: number, infants: number, checkin: string, checkout: string) =>
  pmsAnonymousPersons(adults, children, infants).map((p) => newPerson(p.ageBand, checkin, checkout));

/** « Adulte 2 » for an unnamed person — its position among the same kind. */
export function personLabel(persons: readonly PersonDraft[], index: number): string {
  const p = persons[index];
  if (p === undefined) return '';
  if (p.name.trim() !== '') return p.name.trim();
  const kind = p.ageBand === 'adult' ? 'Adulte' : p.ageBand === 'infant' ? 'Bébé' : 'Enfant';
  const same = persons.slice(0, index + 1).filter((q) =>
    p.ageBand === 'adult' ? q.ageBand === 'adult' : p.ageBand === 'infant' ? q.ageBand === 'infant' : q.ageBand !== 'adult' && q.ageBand !== 'infant',
  );
  return `${kind} ${same.length}`;
}

export const roomCounts = (room: Pick<RoomDraft, 'persons'>) => pmsCountsFromPersons(room.persons);

/** Is this exception's date one the person can eat that meal on? A dinner on a
 *  night of their stay; a breakfast or a packed lunch on a morning after one. */
const exceptionInStay = (p: Pick<PersonDraft, 'startDate' | 'endDate'>, x: MealException) =>
  x.prestation === 'dinner'
    ? x.serviceDate >= p.startDate && x.serviceDate < p.endDate
    : x.serviceDate > p.startDate && x.serviceDate <= p.endDate;

/**
 * The persons follow a change of the room's dates — the form's mirror of
 * `pms_seed_occupancy` on a customised logement: whoever arrived with the room
 * still does, whoever left with it still does, and everyone is clipped into the
 * new stay. A person the new dates leave with no night is put back on the whole
 * stay rather than dropped (the desk removes people, the dates never do).
 */
function followDates(persons: PersonDraft[], prevIn: string, prevOut: string, nextIn: string, nextOut: string) {
  return persons.map((p) => {
    let s = p.startDate === prevIn ? nextIn : p.startDate;
    let e = p.endDate === prevOut ? nextOut : p.endDate;
    if (s < nextIn) s = nextIn;
    if (e > nextOut) e = nextOut;
    if (e <= s) {
      s = nextIn;
      e = nextOut;
    }
    const moved = { ...p, startDate: s, endDate: e };
    return { ...moved, exceptions: moved.exceptions.filter((x) => exceptionInStay(moved, x)) };
  });
}

// ── Construction ────────────────────────────────────────────────────────────

export const emptyRoom = (todayIso: string): RoomDraft => {
  const checkout = isoAddDays(todayIso, 1);
  return {
    id: null,
    roomTypeId: '',
    soldRoomTypeId: '',
    ratePlanId: '',
    roomId: '',
    checkin: todayIso,
    checkout,
    persons: personsFromCounts(2, 0, 0, todayIso, checkout),
    personsLoaded: true,
    meals: [],
    occupantName: '',
    origStatus: null,
    origCheckin: todayIso,
    origCheckout: checkout,
    priceEuros: '',
    nights: [],
    products: [],
  };
};

export const emptyForm = (todayIso: string): FormState => ({
  id: null,
  guestId: '',
  lastName: '',
  firstName: '',
  email: '',
  phone: '',
  lang: 'fr',
  rooms: [emptyRoom(todayIso)],
  status: 'confirmed',
  channel: '',
  channelRef: '',
  notes: '',
  groupName: '',
});

/**
 * Editing an existing booking, from its logements.
 *
 * `stays` is REQUIRED: a fallback would have to invent one room from the
 * booking's derived columns, and would do it silently on exactly the
 * multi-room booking it is wrong for. RELEASED logements are dropped: the form
 * edits what the booking still holds.
 *
 * The persons are a STAND-IN from each room's counts until `withPersons`
 * replaces them (`personsLoaded` false), so a form opened before that read
 * returns still renders the right head counts and never saves a guess.
 */
export const formFromReservation = (r: ReservationView, stays: readonly RoomStayView[]): FormState => ({
  id: r.id,
  guestId: r.guestId,
  lastName: r.guest?.lastName ?? '',
  firstName: r.guest?.firstName ?? '',
  email: r.guest?.email ?? '',
  phone: r.guest?.phone ?? '',
  lang: r.guest?.lang ?? 'fr',
  rooms: stays.filter((s) => s.cancelledAt === null).map(roomFromStay),
  status: r.status === 'reserved' ? 'reserved' : 'confirmed',
  channel: r.channel ?? '',
  channelRef: r.channelRef ?? '',
  notes: r.notes ?? '',
  groupName: r.groupName ?? '',
});

const roomFromStay = (s: RoomStayView): RoomDraft => ({
  id: s.id,
  roomTypeId: String(s.roomTypeId),
  soldRoomTypeId: s.soldRoomTypeId == null ? '' : String(s.soldRoomTypeId),
  ...(s.fromVega === true ? { fromVega: true } : {}),
  ...(s.cancellationTerms === undefined ? {} : { cancellationTerms: s.cancellationTerms }),
  ratePlanId: String(s.ratePlanId),
  roomId: s.roomId === null ? '' : String(s.roomId),
  checkin: s.checkinDate,
  checkout: s.checkoutDate,
  persons: personsFromCounts(s.adults, s.children, s.infants, s.checkinDate, s.checkoutDate),
  personsLoaded: false,
  meals: s.meals === undefined ? null : [...s.meals],
  occupantName: s.occupantName ?? '',
  origStatus: s.status,
  origCheckin: s.checkinDate,
  origCheckout: s.checkoutDate,
  // The fallback is left EMPTY when editing, deliberately: the real per-night
  // prices come from the stay's charge lines, through `withChargeDays`.
  priceEuros: '',
  nights: [],
  products: [],
});

/**
 * Put the stored persons and their meal exceptions into an edit form.
 *
 * A room the read returned no persons for keeps its stand-in and stays
 * `personsLoaded: false` — every logement has persons since step 1's backfill,
 * so an empty answer means « not readable », and the save must not turn it
 * into a list.
 *
 * `needs` is passed ONLY by a desk holding `pms.needs.read`, and only once read
 * (step 5): every loaded person then has a `health`, empty when nothing is
 * declared. Omitted, every `health` stays null and nothing about needs is sent.
 */
export function withPersons(
  form: FormState,
  persons: readonly StayPerson[],
  meals: readonly StayPersonMeal[],
  needs?: readonly StayPersonNeeds[],
): FormState {
  return {
    ...form,
    rooms: form.rooms.map((room) => {
      const mine = persons
        .filter((p) => p.roomStayId === room.id)
        .sort((a, b) => a.position - b.position);
      if (mine.length === 0) return room;
      return {
        ...room,
        personsLoaded: true,
        persons: mine.map((p) => ({
          key: nextKey(),
          id: p.id,
          name: p.name ?? '',
          ageBand: p.ageBand,
          startDate: p.startDate,
          endDate: p.endDate,
          exceptions: meals
            .filter((m) => m.personId === p.id)
            .map((m) => ({ serviceDate: m.serviceDate, prestation: m.prestation, taken: m.taken })),
          health: needs === undefined ? null : healthOf(needs.find((n) => n.personId === p.id)),
        })),
      };
    }),
  };
}

const healthOf = (n: StayPersonNeeds | undefined): PersonHealth => ({
  needs: n?.needs ?? '',
  allergens: n === undefined ? [] : [...n.allergens],
});

/**
 * Fill an edit form with the booking's ACTIVE charge lines, ROOM BY ROOM.
 *
 * Auto-applied products are NOT put back into `products`: they are re-expanded
 * by the writer from the catalogue on every save. Lines DERIVED from persons
 * (a breakfast billed per person, the levy) are not either, for the same
 * reason: the persons and the switches are what produce them. A board product
 * typed at the desk before step 4 (« PDJ × 2 ») is an agreed line and stays
 * one, shown among the extras, until the desk replaces it with a switch.
 */
export function withChargeDays(
  form: FormState,
  charges: readonly ChargeDay[],
  catalogue: readonly { id: number; autoApply: boolean; perPerson?: boolean }[],
): FormState {
  return {
    ...form,
    rooms: form.rooms.map((room) => {
      const mine = charges.filter((c) => c.roomStayId === room.id);

      // The quantity rides with the price: a group priced per person (« 2 × 75 € »)
      // or several rooms Vega left on one logement would otherwise reload — and
      // save — as ONE unit at the unit price (CADAY ROUGE 024245: 374,82 € on the
      // form against 1 694,82 € stored).
      const nights: NightPrice[] = mine
        .filter((c) => c.kind === 'room')
        .map((c) =>
          c.quantity === 1
            ? { date: c.date, priceCents: c.unitCents }
            : { date: c.date, priceCents: c.unitCents, quantity: c.quantity },
        )
        .sort((a, b) => a.date.localeCompare(b.date));

      const asked = new Map<number, number>();
      for (const c of mine) {
        if (c.kind !== 'product' || c.productId === null) continue;
        const product = catalogue.find((p) => p.id === c.productId);
        if (product?.autoApply) continue;
        if (c.origin === 'derived') continue;
        // One entry per product, not per line: the same extra appears once per
        // night it bills on, and they are the same request.
        if (!asked.has(c.productId)) asked.set(c.productId, c.quantity);
      }

      return {
        ...room,
        nights,
        products: [...asked].map(([productId, quantity]) => ({ productId, quantity })),
      };
    }),
  };
}

/**
 * Re-cut a room's per-night vector for its current dates. Prices already typed
 * for dates still inside the stay SURVIVE.
 */
export function syncRoomNights(room: RoomDraft): RoomDraft {
  const fallback = parseEurosToCents(room.priceEuros) ?? 0;
  return { ...room, nights: pmsNightVector(room.checkin, room.checkout, room.nights, fallback) };
}

/** Apply a patch to one room by index, leaving its siblings untouched. A date
 *  change carries the persons with it. */
export function patchRoom(form: FormState, index: number, patch: Partial<RoomDraft>): FormState {
  return {
    ...form,
    rooms: form.rooms.map((r, i) => (i === index ? applyRoomPatch(r, patch) : r)),
  };
}

function applyRoomPatch(r: RoomDraft, patch: Partial<RoomDraft>): RoomDraft {
  const next = { ...r, ...patch };
  const datesMoved =
    (patch.checkin !== undefined && patch.checkin !== r.checkin) ||
    (patch.checkout !== undefined && patch.checkout !== r.checkout);
  if (datesMoved && patch.persons === undefined && pmsNightCount(next.checkin, next.checkout) > 0) {
    next.persons = followDates(r.persons, r.checkin, r.checkout, next.checkin, next.checkout);
  }
  return syncRoomNights(next);
}

/**
 * Add a room, copied from the LAST one rather than blank — a second room of
 * the same booking almost always shares its dates. NOT copied: the stored id,
 * the assigned unit, the occupant, the priced nights, and the persons'
 * identities (fresh anonymous persons of the same bands, the whole stay).
 */
export function addRoom(form: FormState): FormState {
  const last = form.rooms[form.rooms.length - 1];
  if (last === undefined) return form;
  return {
    ...form,
    rooms: [
      ...form.rooms,
      syncRoomNights({
        ...last,
        id: null,
        roomId: '',
        occupantName: '',
        origStatus: null,
        origCheckin: last.checkin,
        origCheckout: last.checkout,
        nights: [],
        persons: last.persons.map((p) => newPerson(p.ageBand, last.checkin, last.checkout)),
        personsLoaded: true,
        meals: last.meals === null ? [] : [...last.meals],
      }),
    ],
  };
}

/** Remove a room. The LAST one cannot go. */
export function removeRoom(form: FormState, index: number): FormState {
  if (form.rooms.length <= 1) return form;
  return { ...form, rooms: form.rooms.filter((_, i) => i !== index) };
}

/** A click on a free cell in the rack: this unit, this night. */
export const formFromPlanCell = (room: Room, dayIso: string): FormState => {
  const base = emptyForm(dayIso);
  return {
    ...base,
    rooms: [
      {
        ...emptyRoom(dayIso),
        roomTypeId: room.roomTypeId === null ? '' : String(room.roomTypeId),
        roomId: String(room.id),
      },
    ],
  };
};

// ── Persons, edited ─────────────────────────────────────────────────────────

export function patchPerson(room: RoomDraft, key: string, patch: Partial<Omit<PersonDraft, 'key' | 'id'>>): RoomDraft {
  return {
    ...room,
    persons: room.persons.map((p) => {
      if (p.key !== key) return p;
      const next = { ...p, ...patch };
      return { ...next, exceptions: next.exceptions.filter((x) => exceptionInStay(next, x)) };
    }),
  };
}

/** « + Personne »: an anonymous adult for the whole stay. */
export const addPerson = (room: RoomDraft, ageBand: AgeBand = 'adult'): RoomDraft => ({
  ...room,
  persons: [...room.persons, newPerson(ageBand, room.checkin, room.checkout)],
});

/** Remove a person — never the last: a logement always has one (§4). */
export function removePerson(room: RoomDraft, key: string): RoomDraft {
  if (room.persons.length <= 1) return room;
  return { ...room, persons: room.persons.filter((p) => p.key !== key) };
}

/** A person's free-text needs (step 5). */
export function setPersonNeeds(room: RoomDraft, key: string, needs: string): RoomDraft {
  return {
    ...room,
    persons: room.persons.map((p) =>
      p.key === key ? { ...p, health: { needs, allergens: p.health?.allergens ?? [] } } : p,
    ),
  };
}

/** Tick or untick one of the 14 allergens for a person (step 5). */
export function togglePersonAllergen(room: RoomDraft, key: string, allergen: Allergen): RoomDraft {
  return {
    ...room,
    persons: room.persons.map((p) => {
      if (p.key !== key) return p;
      const current = p.health?.allergens ?? [];
      const allergens = current.includes(allergen) ? current.filter((a) => a !== allergen) : [...current, allergen];
      return { ...p, health: { needs: p.health?.needs ?? '', allergens } };
    }),
  };
}

/**
 * Flip what ONE person takes on ONE date. The result is stored as an
 * exception only when it differs from the room's default; a flip back to the
 * default removes the exception (so the person follows the room again).
 */
export function togglePersonMeal(
  room: RoomDraft,
  board: PmsBoard,
  key: string,
  serviceDate: string,
  meal: MealPrestation,
): RoomDraft {
  const fallback = pmsMealDefault(board, room.meals ?? [], meal);
  return {
    ...room,
    persons: room.persons.map((p) => {
      if (p.key !== key) return p;
      const x = p.exceptions.find((e) => e.serviceDate === serviceDate && e.prestation === meal);
      const nowTaken = !(x === undefined ? fallback : x.taken);
      const rest = p.exceptions.filter((e) => !(e.serviceDate === serviceDate && e.prestation === meal));
      return { ...p, exceptions: nowTaken === fallback ? rest : [...rest, { serviceDate, prestation: meal, taken: nowTaken }] };
    }),
  };
}

// ── The bulk actions (« Pour tous », « Sélection ») — draft edits only ──────

/** Switch a meal on or off for these rooms. Persons' exceptions are left
 *  alone (Q6). */
export function setRoomsMeal(form: FormState, indices: readonly number[], meal: MealPrestation, on: boolean): FormState {
  return {
    ...form,
    rooms: form.rooms.map((r, i) => {
      if (!indices.includes(i)) return r;
      const current = r.meals ?? [];
      const meals = on ? [...new Set([...current, meal])] : current.filter((m) => m !== meal);
      return { ...r, meals };
    }),
  };
}

/** « Dates… »: these rooms take these dates; their persons follow. A room
 *  whose guest has arrived keeps its arrival (the edit rule) and is not
 *  shortened. */
export function setRoomsDates(form: FormState, indices: readonly number[], checkin: string, checkout: string): FormState {
  if (pmsNightCount(checkin, checkout) <= 0) return form;
  return {
    ...form,
    rooms: form.rooms.map((r, i) => {
      if (!indices.includes(i)) return r;
      const arrived = r.origStatus === 'checked_in';
      const nextIn = arrived ? r.checkin : checkin;
      const nextOut = arrived && checkout < r.origCheckout ? r.checkout : checkout;
      if (pmsNightCount(nextIn, nextOut) <= 0) return r;
      return applyRoomPatch(r, { checkin: nextIn, checkout: nextOut });
    }),
  };
}

/** « Prix… »: every night of these rooms at this price — in the unit the €/nuit
 *  column shows it in. A room read « 75,00 /pers. » typed 80 becomes 80 per
 *  person, not 80 for the room: each night keeps its quantity. */
export function setRoomsPrice(form: FormState, indices: readonly number[], euros: string): FormState {
  const cents = parseEurosToCents(euros);
  if (cents === null) return form;
  return {
    ...form,
    rooms: form.rooms.map((r, i) =>
      indices.includes(i)
        ? {
            ...r,
            priceEuros: euros,
            nights: pmsNightVector(r.checkin, r.checkout, r.nights.map((n) => ({ ...n, priceCents: cents })), cents),
          }
        : r,
    ),
  };
}

/** « + Prestation »: an agreed extra on these rooms (quantity 1 unless it is
 *  already there). */
export function addRoomsProduct(form: FormState, indices: readonly number[], productId: number): FormState {
  return {
    ...form,
    rooms: form.rooms.map((r, i) =>
      indices.includes(i) && !r.products.some((p) => p.productId === productId)
        ? { ...r, products: [...r.products, { productId, quantity: 1 }] }
        : r,
    ),
  };
}

export function setRoomProductQty(room: RoomDraft, productId: number, quantity: number): RoomDraft {
  const rest = room.products.filter((p) => p.productId !== productId);
  return { ...room, products: quantity > 0 ? [...rest, { productId, quantity }] : rest };
}

/** The group's dates: the earliest arrival and the latest departure. A row
 *  shows its own only when they differ (§2.4). */
export function groupSpan(rooms: readonly Pick<RoomDraft, 'checkin' | 'checkout'>[]): { checkin: string; checkout: string } {
  let checkin = rooms[0]?.checkin ?? '';
  let checkout = rooms[0]?.checkout ?? '';
  for (const r of rooms) {
    if (r.checkin < checkin) checkin = r.checkin;
    if (r.checkout > checkout) checkout = r.checkout;
  }
  return { checkin, checkout };
}

/** The type de séjour of a room: its own plan, or the default for a new one. */
export function roomBoard(room: Pick<RoomDraft, 'ratePlanId'>, plans: readonly RatePlan[]): PmsBoard {
  const plan = room.ratePlanId === '' ? plans[0] : plans.find((p) => String(p.id) === room.ratePlanId);
  return plan?.board ?? 'none';
}

// ── Validation and the payload ──────────────────────────────────────────────

/** The form is submittable. Guest identity, then EVERY room priced and
 *  categorised — one unpriced room among four must block the save — and every
 *  room with at least one adult (the logement's own rule). A first name is
 *  required for a NEW client only (PMS-CARDEX-BRIEF §6.1). */
export function formIsComplete(f: FormState): boolean {
  if (f.lastName.trim() === '') return false;
  if (f.guestId === '' && f.firstName.trim() === '') return false;
  if (f.rooms.length === 0) return false;
  return f.rooms.every(
    (r) =>
      r.roomTypeId !== '' &&
      // `some(> 0)`, not `nights.length > 0`: `patchRoom` re-cuts the vector on
      // every edit, so a room with a blank fallback carries a full set of nights
      // priced at ZERO — « nobody has said what this costs ». A comped room is
      // still expressible: type 0 in the fallback, a statement not an absence.
      (r.priceEuros !== '' || r.nights.some((n) => n.priceCents > 0)) &&
      pmsNightCount(r.checkin, r.checkout) > 0 &&
      roomCounts(r).adults >= 1,
  );
}

export interface ReservationWrite {
  guest: {
    id: string | null;
    draft: {
      lastName: string;
      firstName: string;
      email: string | null;
      phone: string | null;
      lang: Guest['lang'];
      notes: null;
    };
  };
  fields: {
    roomTypeId: number;
    roomId: number | null;
    checkin: string;
    checkout: string;
    adults: number;
    children: number;
    channel: string | null;
    channelRef: string | null;
    ratePlanId: number;
    priceCents: number;
    nights: { date: string; priceCents: number }[];
    products: { productId: number; quantity: number }[];
    rooms: RoomStayInput[];
    notes: string | null;
  };
  status: 'reserved' | 'confirmed';
}

/** A person → the command's shape. Exceptions outside the person's stay are
 *  dropped (the database would refuse them); an empty name is null. Needs and
 *  allergens go only when the form holds them (`health` set): absent keys keep
 *  what is stored, and `null` + `[]` erase it. */
const personPayload = (p: PersonDraft): StayPersonInput => ({
  id: p.id,
  name: p.name.trim() === '' ? null : p.name.trim(),
  ageBand: p.ageBand,
  startDate: p.startDate,
  endDate: p.endDate,
  meals: p.exceptions.filter((x) => exceptionInStay(p, x)),
  ...(p.health === null
    ? {}
    : { needs: p.health.needs.trim() === '' ? null : p.health.needs.trim(), allergens: [...p.health.allergens] }),
});

/**
 * Form → what the services take, or a French error naming what is wrong.
 *
 * `plan` is the établissement's DEFAULT, used for a room being created; a
 * stored room keeps its own. `rooms` is what the RPC reads; the flat fields
 * beside it mirror the FIRST room (`pms_book_reservation` still accepts them).
 */
export function buildReservationWrite(
  form: FormState,
  plan: RatePlan | null,
): { ok: true; write: ReservationWrite } | { ok: false; error: string } {
  if (plan === null) {
    return {
      ok: false,
      error: "Aucun plan tarifaire n'existe pour cet établissement — la réservation ne peut pas être enregistrée.",
    };
  }
  if (form.rooms.length === 0) {
    return { ok: false, error: 'Une réservation doit compter au moins un logement.' };
  }

  const rooms: RoomStayInput[] = [];
  for (const [i, room] of form.rooms.entries()) {
    const where = form.rooms.length > 1 ? ` (logement ${i + 1})` : '';

    const typed = room.priceEuros === '' ? null : parseEurosToCents(room.priceEuros);
    if (room.priceEuros !== '' && typed === null) {
      return { ok: false, error: `Prix par nuit invalide${where} (ex. 95 ou 95,50)` };
    }
    if (room.roomTypeId === '') {
      return { ok: false, error: `Catégorie requise${where}` };
    }
    const nights = pmsNightVector(room.checkin, room.checkout, room.nights, typed ?? 0);
    if (nights.length === 0) {
      return { ok: false, error: `Le séjour doit compter au moins une nuit${where}.` };
    }
    const counts = roomCounts(room);
    if (counts.adults < 1) {
      return { ok: false, error: `Chaque logement compte au moins un adulte${where}.` };
    }
    const priceCents = typed ?? nights[0]!.priceCents;

    rooms.push({
      id: room.id,
      roomTypeId: Number(room.roomTypeId),
      ratePlanId: room.ratePlanId === '' ? plan.id : Number(room.ratePlanId),
      roomId: room.roomId === '' ? null : Number(room.roomId),
      checkin: room.checkin,
      checkout: room.checkout,
      adults: counts.adults,
      children: counts.children,
      infants: counts.infants,
      occupantName: room.occupantName.trim() === '' ? null : room.occupantName.trim(),
      priceCents,
      nights,
      products: room.products.filter((p) => p.quantity > 0),
      ...(room.personsLoaded ? { persons: room.persons.map(personPayload) } : {}),
      ...(room.meals === null ? {} : { meals: [...room.meals] }),
    });
  }

  const head = rooms[0]!;

  return {
    ok: true,
    write: {
      guest: {
        id: form.guestId || null,
        draft: {
          lastName: form.lastName,
          firstName: form.firstName,
          email: form.email || null,
          phone: form.phone || null,
          lang: (form.lang as Guest['lang']) || 'fr',
          notes: null,
        },
      },
      fields: {
        roomTypeId: head.roomTypeId,
        roomId: head.roomId ?? null,
        checkin: head.checkin,
        checkout: head.checkout,
        adults: head.adults,
        children: head.children,
        channel: form.channel || null,
        channelRef: form.channelRef || null,
        ratePlanId: head.ratePlanId,
        priceCents: head.priceCents,
        nights: head.nights ?? [],
        products: head.products ?? [],
        rooms,
        notes: form.notes || null,
      },
      status: form.status,
    },
  };
}

/** The nights of a stay, for the detail's day squares. */
export function stayNights(checkin: string, checkout: string): string[] {
  return Array.from({ length: Math.max(pmsNightCount(checkin, checkout), 0) }, (_, i) => pmsAddDays(checkin, i));
}

/** True when the desk has changed anything the form would save — the draft no
 *  longer equals what the form opened with. By VALUE, not by a « touched » flag:
 *  typing a figure and putting it back is not a change, and an edit that bypasses
 *  the flag still counts. A draft is plain data (strings, numbers, arrays), so
 *  its JSON is its identity. */
export function formIsDirty(initial: FormState, form: FormState): boolean {
  return form !== initial && JSON.stringify(form) !== JSON.stringify(initial);
}
